import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import "../../styles/admin/TeacherForm.scss";
const TeacherForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isInstitution = false,
}) => {
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "teacher",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!initialData) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      }
    }

    if (!formData.role && !isInstitution) {
      newErrors.role = "Role is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = { ...formData };
    if (initialData) {
      if (!payload.password) delete payload.password;
    }
    onSubmit(payload);
  };

  useEffect(() => {
    if (initialData) {
      const { name, email, role, isDisabled } = initialData;
      setFormData({
        name: name || "",
        email: email || "",
        password: "",
        role: role || "teacher",
      });
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "teacher",
      });
    }
  }, [initialData, isOpen]);

  return (
    <div className="teacher-form-overlay">
      <div className="teacher-form-container">
        <div className="teacher-form-header">
          <h2>{initialData ? "Edit" : "Create"} {formData.role === "admin" ? "Admin" : "Teacher"}</h2>
          <button
            className="teacher-close-button"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="teacher-form">
          <div
            className={`teacher-form-group ${errors.name ? "has-error" : ""}`}
          >
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter full name"
            />
            {errors.name && (
              <span id="name-error" className="teacher-error-message">
                {errors.name}
              </span>
            )}
          </div>

          <div
            className={`teacher-form-group ${errors.email ? "has-error" : ""}`}
          >
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email"
              autoComplete="email"
              disabled={!!initialData}
            />
            {errors.email && (
              <span id="email-error" className="teacher-error-message">
                {errors.email}
              </span>
            )}
          </div>

          {!initialData && (
            <div
              className={`teacher-form-group ${
                errors.password ? "has-error" : ""
              }`}
            >
              <label htmlFor="password">Password</label>
              <div className="password-input">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 5C6.5 5 2.1 8.6 1 12c1.1 3.4 5.5 7 11 7s9.9-3.6 11-7c-1.1-3.4-5.5-7-11-7z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 3l18 18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M12 5C6.5 5 2.1 8.6 1 12c.49 1.5 1.67 3.1 3.36 4.41M20.64 16.41C22.33 15.1 23.51 13.5 24 12c-1.1-3.4-5.5-7-12-7"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                      <path
                        d="M9.5 9.5a3.5 3.5 0 004.95 4.95"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <span id="password-error" className="teacher-error-message">
                  {errors.password}
                </span>
              )}
            </div>
          )}

          {!isInstitution && (
            <div
              className={`teacher-form-group ${errors.role ? "has-error" : ""}`}
            >
              <label>Role</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={formData.role === "admin"}
                    onChange={handleChange}
                  />
                  Admin
                </label>
                <label>
                  <input
                    type="radio"
                    name="role"
                    value="teacher"
                    checked={formData.role === "teacher"}
                    onChange={handleChange}
                  />
                  Teacher
                </label>
              </div>
              {errors.role && (
                <span className="teacher-error-message">{errors.role}</span>
              )}
            </div>
          )}
          <div className="teacher-form-actions">
            <button
              type="button"
              className="teacher-cancel-button "
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="teacher-submit-button btn-interactive"
            >
              {initialData ? "Update" : "Create"} {formData.role === "admin" ? "Admin" : "Teacher"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherForm;
